import { Stack } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useFragment } from "react-relay";
import * as yup from "yup";
import { GraphQLAutomatedNotificationInput } from "../../../../../mutations/__generated__/useAppClipNotificationsUpdateMutation.graphql";
import { GraphQLCDPAudienceInput } from "../../../../../mutations/__generated__/useBrandAppConfigUpdate2Mutation.graphql";
import {
  AutomatedNotificationType,
  AutomatedNotificationsForm_automatedNotifications$key,
} from "./__generated__/AutomatedNotificationsForm_automatedNotifications.graphql";
import SaveButton, { SavedState } from "../../../../../components/SaveButton";
import { KinnAudienceType } from "../../../../../hooks/useBrandAudiences";
import { validateUrl } from "../../../../../utils/validators";
import AutomatedNotificationsGroup from "./AutomatedNotificationsGroup";
import {
  MAX_APP_CLIP_NOTIFICATION_DELAY_MINUTES,
  getDelayOptions,
} from "./delayOptions";

export const AuthStateAudienceSelection = {
  EVERYONE: "Everyone",
  ...KinnAudienceType,
} as const;

type AutomatedNotificationsFormBaseProps = {
  isAppClip?: boolean;
};

const AUTOMATED_NOTIFICATION_FORM_GROUP_NAMES = [
  "abandonedCart",
  "abandonedBrowse",
  "abandonedCheckout",
  "welcomeSeries",
  "reengagement",
] as const;
type AutomatedNotificationFormGroupName =
  typeof AUTOMATED_NOTIFICATION_FORM_GROUP_NAMES[number];

const NOTIFICATION_GROUPS: {
  title: string;
  formName: AutomatedNotificationFormGroupName;
  notificationType: AutomatedNotificationType;
  showsLandingPath: boolean;
}[] = [
  {
    title: "Abandoned Cart",
    formName: "abandonedCart",
    notificationType: "ABANDONED_CART",
    showsLandingPath: false,
  },
  {
    title: "Abandoned Browse",
    formName: "abandonedBrowse",
    notificationType: "ABANDONED_BROWSE",
    showsLandingPath: false,
  },
  {
    title: "Abandoned Checkout",
    formName: "abandonedCheckout",
    notificationType: "ABANDONED_CHECKOUT",
    showsLandingPath: false,
  },
  {
    title: "Welcome Series",
    formName: "welcomeSeries",
    notificationType: "WELCOME_SERIES",
    showsLandingPath: true,
  },
  {
    title: "Reengagement",
    formName: "reengagement",
    notificationType: "REENGAGEMENT",
    showsLandingPath: true,
  },
];

const notifSchema = ({
  isAppClip = false,
}: AutomatedNotificationsFormBaseProps) => {
  return yup
    .object({
      id: yup.string().optional(),
      title: yup
        .string()
        .max(150)
        .when("isEnabled", {
          is: (isEnabled: boolean) => isEnabled === true,
          then: (schema) => schema.required("Please set a title"),
          otherwise: (schema) => schema,
        }),
      message: yup
        .string()
        .max(250)
        .when("isEnabled", {
          is: (isEnabled: boolean) => isEnabled === true,
          then: (schema) => schema.required("Please set a message"),
          otherwise: (schema) => schema,
        }),
      delayMinutes: yup
        .number()
        .typeError("Please set a valid delay")
        .when("isEnabled", {
          is: (isEnabled: boolean) => isEnabled === true,
          then: (schema) =>
            schema
              .min(1, "Must be greater than or equal to 1")
              .max(
                isAppClip ? 8 * 60 : 262980,
                isAppClip
                  ? "Must be less than 8 hours"
                  : "Must be less than 6 months"
              )
              .required("Required"),
          otherwise: (schema) => schema,
        }),
      landingPath: yup
        .string()
        .max(2083)
        .optional()
        .when({
          is: (exists: boolean) => exists !== undefined,
          then: (schema) =>
            schema.when("isEnabled", {
              is: (isEnabled: boolean) => {
                return isEnabled === true;
              },
              then: (schema) =>
                schema
                  .required("Please set a landing path")
                  .test("is-url-valid", "URL is not valid", (value) => {
                    return validateUrl({ url: value });
                  }),
              otherwise: (schema) => schema,
            }),
        }),
      isEnabled: yup.boolean().required(),
      audience: yup
        .string()
        .oneOf(
          [
            AuthStateAudienceSelection.EVERYONE,
            AuthStateAudienceSelection.LOGGED_IN,
            AuthStateAudienceSelection.LOGGED_OUT,
          ],
          "Invalid audience selection"
        )
        .default(AuthStateAudienceSelection.EVERYONE),
    })
    .required();
};

const schema = (props: AutomatedNotificationsFormBaseProps) => {
  return yup
    .object(
      Object.fromEntries(
        NOTIFICATION_GROUPS.map((group) => [
          group.formName,
          yup.array().of(notifSchema(props)),
        ])
      )
    )
    .required();
};

type FormData = yup.InferType<ReturnType<typeof schema>>;

const automatedNotificationsFragment = graphql`
  fragment AutomatedNotificationsForm_automatedNotifications on GraphQLAutomatedNotificationConnection {
    edges {
      node {
        id
        notifType
        title
        message
        isEnabled
        delaySeconds
        landingPath
        audiences {
          audienceId
        }
      }
    }
  }
`;

type AutomatedNotificationsFormProps = AutomatedNotificationsFormBaseProps & {
  appConfigId: string;
  automatedNotifications: AutomatedNotificationsForm_automatedNotifications$key;
  handleSave: (
    automatedNotifications: GraphQLAutomatedNotificationInput[],
    onCompleted: () => void
  ) => void;
  isSaving: boolean;
};

const AutomatedNotificationsForm = ({
  appConfigId,
  automatedNotifications: automatedNotificationsKey,
  handleSave,
  isSaving,
  isAppClip,
}: AutomatedNotificationsFormProps) => {
  const automatedNotifications =
    useFragment<AutomatedNotificationsForm_automatedNotifications$key>(
      automatedNotificationsFragment,
      automatedNotificationsKey
    ).edges;

  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
    reset,
    control,
    watch,
  } = useForm<FormData>({
    defaultValues: Object.fromEntries(
      NOTIFICATION_GROUPS.map((group) => {
        const notifications = automatedNotifications.filter(
          (notif) => notif.node.notifType === group.notificationType
        );
        return [
          group.formName,
          notifications.map((notif) => ({
            id: notif.node.id,
            title: notif.node.title ?? "",
            message: notif.node.message ?? "",
            delayMinutes: notif.node.delaySeconds / 60,
            landingPath: group.showsLandingPath
              ? notif.node.landingPath ?? ""
              : undefined,
            isEnabled: notif.node.isEnabled ?? false,
            audience: (() => {
              const audienceIds =
                notif.node.audiences?.map((a) => a.audienceId) || [];
              if (audienceIds.includes(KinnAudienceType.LOGGED_IN))
                return AuthStateAudienceSelection.LOGGED_IN;
              if (audienceIds.includes(KinnAudienceType.LOGGED_OUT))
                return AuthStateAudienceSelection.LOGGED_OUT;
              return AuthStateAudienceSelection.EVERYONE;
            })(),
          })),
        ];
      })
    ),
    resolver: yupResolver(schema({ isAppClip })),
  });

  const saveButtonState = useMemo(() => {
    if (isSaving) {
      return SavedState.SAVING;
    }
    if (isDirty) {
      return SavedState.ENABLED;
    }
    return SavedState.SAVED;
  }, [isSaving, isDirty]);

  const onSubmit = (data: FormData) => {
    const allNotifGroups = NOTIFICATION_GROUPS.map((group) => {
      return {
        notifType: group.notificationType,
        notifications: data[group.formName] ?? [],
      };
    });
    const automatedNotifs: GraphQLAutomatedNotificationInput[] =
      allNotifGroups.flatMap((group) => {
        return group.notifications.map((notif) => {
          let audience: GraphQLCDPAudienceInput | undefined = undefined;
          if (notif.audience === AuthStateAudienceSelection.LOGGED_IN) {
            audience = {
              audienceId: KinnAudienceType.LOGGED_IN,
              audienceName: KinnAudienceType.LOGGED_IN,
              audienceType: "SEGMENT",
              cdpType: "KINN",
            };
          } else if (notif.audience === AuthStateAudienceSelection.LOGGED_OUT) {
            audience = {
              audienceId: KinnAudienceType.LOGGED_OUT,
              audienceName: KinnAudienceType.LOGGED_OUT,
              audienceType: "SEGMENT",
              cdpType: "KINN",
            };
          }
          return {
            id: notif.id,
            notifType: group.notifType,
            title: notif.title ?? "",
            message: notif.message ?? "",
            delaySeconds: (notif.delayMinutes ?? 0) * 60,
            landingPath: notif.landingPath,
            isEnabled: notif.isEnabled,
            audiences: audience ? [audience] : [],
          };
        });
      });

    handleSave(automatedNotifs, () => {
      reset({}, { keepValues: true });
    });
  };

  const delayOptions = getDelayOptions({
    maxDelayMinutes: isAppClip
      ? MAX_APP_CLIP_NOTIFICATION_DELAY_MINUTES
      : undefined,
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    >
      <Stack spacing={3} width="100%">
        {NOTIFICATION_GROUPS.map((group) => (
          <AutomatedNotificationsGroup
            key={group.notificationType}
            title={group.title}
            formName={group.formName}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
            delayOptions={delayOptions}
            isAppClip={isAppClip}
            showLandingPath={group.showsLandingPath}
          />
        ))}
        <Stack direction="row" justifyContent="center">
          <SaveButton savedState={saveButtonState} useSubmit={true} />
        </Stack>
      </Stack>
    </form>
  );
};

export default AutomatedNotificationsForm;
